import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { PostPlatformSettingsMap } from 'fym-shared/src/music24/postPlatformSettings';
import { useCreatePromoCaptionsMutation, useCreatePromoPostsMutation } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { platformMeta } from '../platformMeta';
import { SocialPlatform } from '../types';
import { ComposerDraft } from './useComposerDraft';
import { StudioActionFeedback } from '../studio/studioActionErrorText';
import { composerAiErrorFeedback } from './composerAiError';

interface UseComposerActionsProps {
  accountId: number;
  draft: ComposerDraft;
}

/** The platform whose ceiling is lowest — copy that fits it fits everywhere. */
function tightest(platforms: SocialPlatform[]): SocialPlatform | undefined {
  return platforms.reduce<SocialPlatform | undefined>(
    (best, one) =>
      best === undefined || platformMeta[one].captionLimit < platformMeta[best].captionLimit ? one : best,
    undefined
  );
}

/**
 * The Composer's three writes: write the master copy, tailor it per channel,
 * and create the posts.
 *
 * Publishing is one call. The endpoint takes a time per channel, so five
 * channels at five different hours are still a single request — a half-created
 * campaign is not a state the artist can be left in.
 */
export function useComposerActions({ accountId, draft }: UseComposerActionsProps) {
  const intl = useIntl();
  const [createCaptions, { isLoading: isWriting }] = useCreatePromoCaptionsMutation();
  const [createPosts, { isLoading: isPublishing, error: postsError }] = useCreatePromoPostsMutation();
  // A failed AI write shows next to the AI buttons, not in the banner of the page (QA finding
  // B12). The next action clears it: another AI write, a send, or a new media.
  const [aiError, setAiError] = useState<StudioActionFeedback | null>(null);
  const clearAiError = useCallback(() => setAiError(null), []);

  const {
    release,
    platforms,
    master,
    overrides,
    media,
    format,
    timing,
    pickedAt,
    bestSlots,
    setMaster,
    applyOverrides,
  } = draft;

  const writeMaster = useCallback(async (): Promise<void> => {
    const target = tightest(platforms);
    if (release === undefined || target === undefined) {
      return;
    }
    setAiError(null);
    const result = await createCaptions({
      accountId,
      trackTitle: release.title,
      artistName: release.artistName,
      releaseDate: release.releaseDate,
      platforms: [target],
    });
    if (isSuccessfulResult(result)) {
      const written = result.data.captions[target];
      if (written !== undefined) {
        setMaster(written);
      }
      return;
    }
    setAiError(composerAiErrorFeedback(intl, result.error));
  }, [accountId, createCaptions, intl, platforms, release, setMaster]);

  const tailorPerPlatform = useCallback(async (): Promise<void> => {
    if (release === undefined || platforms.length === 0) {
      return;
    }
    setAiError(null);
    const result = await createCaptions({
      accountId,
      trackTitle: release.title,
      artistName: release.artistName,
      releaseDate: release.releaseDate,
      platforms,
    });
    if (isSuccessfulResult(result)) {
      applyOverrides(result.data.captions);
      return;
    }
    setAiError(composerAiErrorFeedback(intl, result.error));
  }, [accountId, applyOverrides, createCaptions, intl, platforms, release]);

  const publish = useCallback(
    async (asDraft: boolean, platformSettings?: PostPlatformSettingsMap): Promise<boolean> => {
      if (platforms.length === 0 || master.trim() === '') {
        return false;
      }
      setAiError(null);
      const perChannelCaptions: Partial<Record<SocialPlatform, string>> = {};
      platforms.forEach((platform) => {
        const override = overrides[platform];
        if (override !== undefined) {
          perChannelCaptions[platform] = override;
        }
      });

      const slots = Object.values(bestSlots)
        .filter((slot): slot is string => slot !== undefined)
        .sort((a, b) => a.localeCompare(b));
      let scheduledAt: string;
      if (timing === 'now') {
        scheduledAt = 'now';
      } else if (timing === 'pick') {
        scheduledAt = pickedAt === '' ? 'now' : new Date(pickedAt).toISOString();
      } else {
        scheduledAt = slots[0] ?? 'now';
      }

      const result = await createPosts({
        accountId,
        channelPlatforms: platforms,
        caption: master,
        perChannelCaptions: Object.keys(perChannelCaptions).length > 0 ? perChannelCaptions : undefined,
        perChannelScheduledAt: timing === 'best' ? bestSlots : undefined,
        mediaFileId: media?.fileId,
        smartLinkId: release?.smartLinkId,
        format,
        scheduledAt,
        asDraft: asDraft ? true : undefined,
        platformSettings,
      });
      return isSuccessfulResult(result);
    },
    [accountId, bestSlots, createPosts, format, master, media, overrides, pickedAt, platforms, release, timing]
  );

  return {
    writeMaster,
    tailorPerPlatform,
    publish,
    isWriting,
    isPublishing,
    /** The error of the last send. The AI errors are in `aiError`. */
    error: postsError,
    aiError,
    clearAiError,
  };
}
